import { Company } from "@models/company";
import { UserRole } from "@models/user";
import { Pod } from "@models/pod-filter";
import { DataFilter } from "@core/components/dynamic-table/dynamic-table.component";
import { DCCompanyType } from "src/app/services/companies/companies.service";

export enum TabType {
  SavedSearch,
  AiSummary,
  ProspectUpdate,
  PortfolioUpdate,
  IRUpdate,
  NewsletterUpdate,
  AdminPanel,
  UserPanel,
  AlertPanel,
  DeepChat,
  Dashboard,
  Travel,
}

export type TabRoute =
  | ""
  | "companies"
  | "elevations"
  | "pipeline"
  | "priority"
  | "ic-voting"
  | "history"
  | "data-quality"
  | "ai-summary"
  | "saved-search"
  | "portfolio-update"
  | "prospect-update"
  | "weekly-update"
  | "ir-update"
  | "newsletter-update"
  | "admin-panel"
  | "alert"
  | "chatbot"
  | "interactions"
  | "dashboard"
  | "travel"
  | "dashboard"
  | "combined"
  | "ic"
  | "companies/interactions/review"

export interface GridFilter {
  activePod: Pod;
}

export interface TabData<T>{
  route: TabRoute;
  ordinal?: number;
  label: string;
  hidden?: boolean;
  disabled: boolean;
  tableClass?: string;
  mobileHidden?: boolean;
  mainFilterRaw: DataFilter;
}

export const firmPod: Pod = {
  name: "Firm",
  members: [],
};

export const myCompaniesPod: Pod = {
  name: "My Companies",
  members: [],
  assigneeFilterType: undefined,
};

export const myInteractionsPod: Pod = {
  name: "My Interactions",
  members: [],
  assigneeFilterType: undefined,
};

export const customPod: Pod = {
  name: "Custom filter",
  inactive: true,
  members: [], // userId: 0 => Show all
};

const dashboardTab: TabData<Company> = {
  route: "dashboard",
  ordinal: 8,
  label: "Analytics",
  disabled: false,
  mainFilterRaw: {},
};

const travelTab: TabData<Company> = {
  route: "travel",
  ordinal: 4,
  label: "Travel",
  disabled: false,
  mainFilterRaw: {},
};

const pipelineTab: TabData<Company> = {
  route: "companies",
  ordinal: 1,
  label: "Pipeline",
  disabled: false,
  mainFilterRaw: {    
    pageNumber: 1,
    pageSize: 100,
    sortDirection: "asc",
    sortProperty: "status",
    thenSortDirection: "asc",
    thenSortProperty: "name",
    isActive: "true",
  },
};

// const pipeline: TabData<Company> = {
//   route: "pipeline",
//   ordinal: 2,
//   label: "Pipeline",
//   tableClass: "pipeline-table",
//   disabled: false,
//   mainFilterRaw: {},
// };

const elevationTab: TabData<Company> = {
  route: "elevations",
  ordinal: 3,
  label: "Elevations",
  tableClass: "elevation-table",
  disabled: false,
  mainFilterRaw: {
  },
};

// const priority: TabData<Company> = {
//   route: "priority",
//   ordinal: 3,
//   label: "Priority",
//   disabled: false,
//   mainFilterRaw: {},
// };

const icVoting: TabData<Company> = {
  route: "ic",
  ordinal: 5,
  label: "IC",
  tableClass: "voting-table",
  disabled: false,
  mainFilterRaw: {},
};

const dataQuality: TabData<Company> = {
  route: "data-quality",
  ordinal: 2,
  label: "Data Quality Queue",
  tableClass: "data-quality",
  disabled: false,
  mainFilterRaw: {},
};

const weeklyUpdate: TabData<Company> = {
  route: "weekly-update",
  ordinal: 7,
  label: "Updates",
  tableClass: "ai-summary",
  disabled: false,
  mainFilterRaw: {},
};

const adminPanel: TabData<Company> = {
  route: "admin-panel",
  ordinal: 10,
  label: "Admin",
  disabled: false,
  tableClass: "saved-search",
  mainFilterRaw: {},
};

const deepChat: TabData<Company> = {
  route: "chatbot",
  ordinal: 6,
  label: "Tech Analyst",
  disabled: false,
  mainFilterRaw: {},
};

const interactionLog: TabData<Company> = {
  route: "companies/interactions/review",
  ordinal: 9,
  label: "Interaction Log",
  disabled: false,
  mainFilterRaw: {},
};

const rolePermissions: { [key: string]: TabData<Company>[] } = {
  admin: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, dashboardTab, interactionLog, adminPanel, deepChat,],
  inventmentcommitteadmin: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, interactionLog, deepChat],
  investor: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, interactionLog, deepChat],
  votercommittee: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, interactionLog, deepChat],
  analyst: [pipelineTab, travelTab,],
  investorrelation: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, interactionLog, deepChat],
  newslettermoderator: [pipelineTab, elevationTab, travelTab, icVoting, weeklyUpdate, interactionLog, deepChat],
};

function getUserTabs(
  rolePermissions: { [key: string]: TabData<Company>[] },
  userRoles: UserRole[],
): TabData<Company>[] {
  const unionOfRolePermissions = userRoles.reduce((accumulator: TabData<Company>[], currentRole: string) => {
    const stringsForRole = rolePermissions[currentRole] || [];
    return [...accumulator, ...stringsForRole];
  }, []);
  return Array.from(new Set(unionOfRolePermissions)).sort((a, b) => a.ordinal! - b.ordinal!);
}

export function getTabs(role: UserRole, userRoles: UserRole[]): TabData<Company>[] {
  return getUserTabs(rolePermissions, userRoles);
}
